/**
 * Returns an array of objects containing general animal information.
 * @function
 *
 * @param {object} data - The data object containing animal information.
 * @param {function} t - The translation function.
 * @returns {Array} - An array of objects with label, span, and content properties.
 */
export const generalAnimalInfo = (data, t) => {
  const { type, name, age, breed, number } = data;

  return [
    {
      label: 'animals.form.type',
      span: 1,
      content: type ? t(`animals.tags.${type}`) : '-'
    },
    {
      hidden: !['DOG', 'CAT'].includes(type),
      label: 'animals.form.name',
      span: 1,
      content: name || '-'
    },
    {
      hidden: !['DOG', 'CAT'].includes(type),
      label: 'animals.form.age',
      span: 1,
      content: age ? t('animals.form.age_label', { number: age }) : '-'
    },
    {
      hidden: ['DOG', 'CAT'].includes(type),
      label: 'animals.form.number',
      span: 1,
      content: number || '-'
    },
    {
      hidden: !['DOG', 'CAT'].includes(type),
      label: ['DOG', 'CAT'].includes(type)
        ? 'animals.form.breed'
        : 'animals.form.species',
      span: 1,
      content: breed || '-'
    }
  ];
};

/**
 * Helper function to create a field object
 * @function
 * @param {string} label - The label key for translation
 * @param {string|number|boolean} value - The value to display
 * @param {function} t - The translation function
 * @param {object} options - Optional parameters for translation
 * @returns {object} Field object
 */
const createField = (label, value, t, options = {}) => {
  if (options.translateKey && value) {
    return {
      label,
      span: 1,
      content: t(options.translateKey, options.params)
    };
  }
  return {
    label,
    span: 1,
    content: value || '-'
  };
};

/**
 * Helper function to create a yes/no field
 * @function
 * @param {string} label - The label key for translation
 * @param {boolean} value - The boolean value
 * @param {function} t - The translation function
 * @returns {object} Field object
 */
const createYesNoField = (label, value, t) => ({
  label,
  span: 1,
  content: value ? t('yes') : t('no')
});

/**
 * Returns fields specific to DOG and CAT types
 * @function
 * @param {object} data - Animal data
 * @param {function} t - Translation function
 * @returns {Array} Array of field objects
 */
const getDogCatFields = (data, t) => {
  const {
    name,
    age,
    breed,
    sex,
    tattoo_number,
    is_animal_leave_property,
    is_runaway,
    in_case_of_absence,
    where_it_sleep,
    brushing,
    diet
  } = data;

  return [
    createField('animals.form.name', name, t),
    createField('animals.form.age', age, t, {
      translateKey: 'animals.form.age_label',
      params: { number: age }
    }),
    createField('animals.form.breed', breed, t),
    createField('animals.form.sex', sex, t),
    createField('animals.form.tattoo_number', tattoo_number, t),
    createYesNoField(
      'animals.form.is_animal_leave_property',
      is_animal_leave_property,
      t
    ),
    createYesNoField('animals.form.is_runaway', is_runaway, t),
    createField('animals.form.in_case_of_absence', in_case_of_absence, t),
    createField('animals.form.where_it_sleep', where_it_sleep, t),
    createField('animals.form.brushing', brushing, t),
    createField('animals.form.diet', diet, t)
  ];
};

/**
 * Returns fields specific to DOG type
 * @function
 * @param {object} data - Animal data
 * @param {function} t - Translation function
 * @returns {Array} Array of field objects
 */
const getDogFields = (data, t) => {
  const { walk_outside_property_number, walk_frequency, walk_duration } = data;

  return [
    createField(
      'animals.form.walk_outside_property_number',
      walk_outside_property_number,
      t
    ),
    createField('animals.form.walk_frequency', walk_frequency, t),
    createField('animals.form.walk_duration', walk_duration, t)
  ];
};

/**
 * Returns fields specific to BIRD type
 * @function
 * @param {object} data - Animal data
 * @returns {Array} Array of field objects
 */
const getBirdFields = (data) => {
  const { cage_cleaning } = data;
  return [
    {
      label: 'animals.form.cage_cleaning',
      span: 1,
      content: (cage_cleaning ? 'Oui' : 'Non') || '-'
    }
  ];
};

/**
 * Returns fields specific to EQUINE and FARM types
 * @function
 * @param {object} data - Animal data
 * @param {function} t - Translation function
 * @returns {Array} Array of field objects
 */
const getEquineFarmFields = (data, t) => {
  const { box_cleaning, is_in_enclosure } = data;
  return [
    createField('animals.form.box_cleaning', box_cleaning, t),
    createField('animals.form.is_in_enclosure', is_in_enclosure, t)
  ];
};

/**
 * Returns common fields for all animal types
 * @function
 * @param {object} data - Animal data
 * @param {function} t - Translation function
 * @returns {Array} Array of field objects
 */
const getCommonFields = (data, t) => {
  const {
    alimentation_type_and_frequency,
    treatment,
    other_animals_presence,
    additional_informations
  } = data || {};

  return [
    createField(
      'animals.form.alimentation_type_and_frequency',
      alimentation_type_and_frequency,
      t
    ),
    createField('animals.form.treatment', treatment, t),
    createField(
      'animals.form.other_animals_presence',
      other_animals_presence,
      t
    ),
    createField(
      'animals.form.additional_informations',
      additional_informations,
      t
    )
  ];
};

/**
 * Returns an array of objects containing the details of an animal.
 * @function
 *
 * @param {object} data - The data object containing the animal information.
 * @param {object} t - The translation object for localization.
 * @returns {Array} - An array of objects containing the animal details.
 */
export const detailAnimalInfo = (data, t) => {
  const { type, number } = data || {};
  const isDogOrCat = ['DOG', 'CAT'].includes(type);

  const baseFields = [
    {
      label: 'animals.form.type',
      span: 1,
      content: (type && t(`animals.tags.${type}`)) || '-'
    }
  ];

  const typeSpecificFields = isDogOrCat
    ? getDogCatFields(data, t)
    : [createField('animals.form.number', number, t)];

  const dogFields = type === 'DOG' ? getDogFields(data, t) : [];
  const birdFields = type === 'BIRD' ? getBirdFields(data) : [];
  const equineFarmFields = ['EQUINE', 'FARM'].includes(type)
    ? getEquineFarmFields(data, t)
    : [];

  return [
    ...baseFields,
    ...typeSpecificFields,
    ...dogFields,
    ...birdFields,
    ...equineFarmFields,
    ...getCommonFields(data, t)
  ];
};
